<template>
    <div 
        class="tw:relative"
        :class="[
            props.customClass,
            props.flat
                ? 'tw:bg-transparent'
                : 'tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200'
        ]"
        :data-cy="props.dataCy"
    >
        <div v-if="props.loading" class="tw:absolute tw:inset-0 tw:bg-white/50 tw:z-10 tw:flex tw:items-center tw:justify-center tw:transition-opacity">
            <svg class="tw:animate-spin tw:h-8 tw:w-8 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
        </div>
        <div v-if="props.title || $slots['header-action']" class="tw:flex tw:justify-between tw:items-end tw:px-6 tw:py-4 tw:border-b tw:border-gray-100">
            <template v-if="props.skeleton">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <div class="tw:h-4 tw:w-24 tw:bg-gray-200 tw:rounded tw:my-1"></div>
                    <div class="tw:h-3 tw:w-44 tw:bg-gray-200 tw:rounded tw:my-1"></div>
                </div>
            </template>
            <template v-else>
                <div class="tw:flex tw:flex-col">
                    <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">{{ props.title }}</h2>
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">{{ props.subtitle }}</p>
                </div>
            </template>
            <slot name="header-action"></slot>
        </div>
        <div class="tw:w-full tw:overflow-x-auto">
            <table class="tw:w-full tw:text-sm tw:border-collapse tw:table-fixed">
                <colgroup>
                    <col
                        v-for="col in props.columns"
                        :key="col.key"
                        :class="col.class"
                        :style="colHasWidth(col) ? '' : `width: ${props.defaultColWidth}`"
                    />
                </colgroup>
                <template v-if="props.skeleton">
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider tw:animate-pulse">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="['tw:px-6 tw:py-3 tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left']">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-header-${col.key}`" :column="col">
                                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
                                    </slot>
                                </div>
                            </th>
                        </tr>
                    </thead>
                </template>
                <template v-else>
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="[col.class || '', 'tw:px-6 tw:py-3 tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left']">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    {{ col.label }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                </template>
                <tbody class="tw:divide-y tw:divide-gray-100">
                    <template v-if="props.skeleton">
                        <tr v-for="n in props.skeletonRows" :key="'sk-' + n">
                            <td v-for="col in props.columns" :key="col.key" :class="['tw:px-6 tw:py-4', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-${col.key}`" :column="col">
                                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-3/4"></div>
                                    </slot>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr v-for="(row, index) in props.data" :key="row._key || row.id || index"  class="hover:tw:bg-gray-50 tw:transition-colors">
                            <td v-for="col in props.columns" :key="col.key" :class="[col.class || '', 'tw:px-6 tw:py-4']">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`cell-${col.key}`" :row="row" :value="getNestedValue(row, col.key)">
                                        <span class="tw:text-gray-700">{{ formatCellValue(getNestedValue(row, col.key)) }}</span>
                                    </slot>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!props.data.length">
                            <td :colspan="props.columns.length" class="tw:px-6 tw:py-10 tw:text-center tw:text-gray-400">
                                {{ props.emptyText }}
                            </td>
                        </tr>
                    </template>
                </tbody>
                <tfoot v-if="$slots.footer && !props.skeleton">
                    <slot name="footer"></slot>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script setup>
    const props = defineProps({
        title:     String,
        subtitle:  String,
        columns:   { type: Array, required: true },
        data:      { type: Array, default: () => [] },
        emptyText: { type: String, default: 'No hay datos disponibles' },
        customClass: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        skeleton: { type: Boolean, default: false },
        skeletonRows: { type: Number, default: 3 },
        dataCy: { type: String, default: '' },
        flat: { type: Boolean, default: false },
        defaultColWidth: {
            type: String,
            default: '100px'
        }
    });

    const colHasWidth = col => /\btw:w-/.test(col.class || '');

    const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => (acc !== null && acc !== undefined ? acc[key] : undefined), obj);

    const formatCellValue = value => (value === null || value === undefined ? '-' : value);
</script>
